import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Package, MapPin, Heart, Settings, ShoppingCart, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { orderAPI, wishlistAPI, authAPI } from '../../api';
import { useCart } from '../../context/CartContext';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function AccountPage() {
  const { user, loading, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2f3e36]"></div>
      </div>
    );
  }

  if (!user) { return null; }

  const tabs = [
    { path: '/account', label: 'Dashboard', icon: Package },
    { path: '/account/orders', label: 'Orders', icon: Package },
    { path: '/account/wishlist', label: 'Wishlist', icon: Heart },
    { path: '/account/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="pw-container py-2 pb-8">
      <Breadcrumbs items={[{ label: 'My Account' }]} />
      <h1 className="text-xl font-semibold tracking-wider uppercase mb-6">My Account</h1>
      <div className="flex flex-col md:flex-row gap-8">
        <aside className="md:w-48 flex-shrink-0">
          <nav className="space-y-1">
            {tabs.map(tab => (
              <Link key={tab.path} to={tab.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname === tab.path ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}>
                <tab.icon size={16} /> {tab.label}
              </Link>
            ))}
          </nav>
        </aside>
        <div className="flex-1 min-w-0">
          <AccountContent />
        </div>
      </div>
    </div>
  );
}

function AccountContent() {
  const location = useLocation();
  const path = location.pathname;

  if (path === '/account/orders') return <OrdersTab />;
  if (path === '/account/wishlist') return <WishlistTab />;
  if (path === '/account/settings') return <SettingsTab />;
  return <DashboardTab />;
}

function DashboardTab() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    orderAPI.getOrders({ limit: 5 }).then(res => setOrders(res.data.orders)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-xl p-6 mb-6">
        <h2 className="text-sm font-semibold mb-2">Welcome, {user.buyer_name || user.email}</h2>
        {user.company_name && <p className="text-sm text-[#6f6a63]">{user.company_name}</p>}
        <p className="text-sm text-[#6f6a63]">{user.email}</p>
      </div>
      <h3 className="text-sm font-semibold tracking-wider uppercase mb-3">Recent Orders</h3>
      {orders.length === 0 ? (
        <p className="text-sm text-[#6f6a63]">No orders yet.</p>
      ) : (
        <div className="space-y-2">
          {orders.map(order => (
            <Link key={order._id} to={`/account/orders`} className="flex items-center justify-between p-3 border border-[#e7e3dd] rounded-lg hover:bg-gray-50">
              <div>
                <p className="text-sm font-medium">{order.order_number}</p>
                <p className="text-xs text-[#6f6a63]">{new Date(order.created_at).toLocaleDateString()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">${order.total?.toFixed(2)}</p>
                <span className={`text-xs capitalize px-2 py-0.5 rounded ${order.status === 'completed' ? 'bg-green-50 text-green-700' : order.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{order.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    orderAPI.getOrders({ limit: 50 }).then(res => { setOrders(res.data.orders); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleReorder = async (orderId) => {
    try {
      await orderAPI.reorder(orderId);
      toast.success('Items added to cart!');
      navigate('/cart');
    } catch (err) {
      toast.error('Failed to reorder');
    }
  };

  if (loading) return <p>Loading orders...</p>;

  return (
    <div>
      <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Order History</h2>
      {orders.length === 0 ? <p className="text-sm text-[#6f6a63]">No orders yet.</p> : (
        <div className="space-y-3">
          {orders.map(order => (
            <div key={order._id} className="border border-[#e7e3dd] rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold">{order.order_number}</p>
                  <p className="text-xs text-[#6f6a63]">{new Date(order.created_at).toLocaleDateString()} | {order.items?.length} items</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">${order.total?.toFixed(2)}</p>
                  <span className={`text-xs capitalize px-2 py-0.5 rounded ${order.status === 'completed' ? 'bg-green-50 text-green-700' : order.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{order.status}</span>
                </div>
              </div>
              <div className="text-xs text-[#6f6a63] mb-3">
                {order.items?.map((item, i) => <span key={i}>{item.product_name} x{item.quantity}{i < order.items.length - 1 ? ', ' : ''}</span>)}
              </div>
              <button onClick={() => handleReorder(order._id)} className="flex items-center gap-1 text-xs text-[#2f6b4f] font-medium hover:underline">
                <RefreshCw size={12} /> Reorder
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WishlistTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    wishlistAPI.getWishlist().then(res => { setItems(res.data.items); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleRemove = async (productId) => {
    try {
      await wishlistAPI.removeFromWishlist(productId);
      setItems(items.filter(i => i._id !== productId));
      toast.success('Removed from wishlist');
    } catch { }
  };

  const handleAddToCart = async (productId) => {
    try {
      await addToCart(productId, 1);
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed'));
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Wishlist</h2>
      {items.length === 0 ? <p className="text-sm text-[#6f6a63]">No items in wishlist.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {items.map(product => (
            <div key={product._id} className="border border-[#e7e3dd] rounded-xl p-3">
              <Link to={`/product/${product.slug}`}>
                <img src={product.images?.[0]} alt={product.name} className="w-full aspect-square object-cover rounded-lg mb-2" />
                <p className="text-sm font-medium">{product.name}</p>
              </Link>
              <div className="flex gap-2 mt-2">
                <button onClick={() => handleAddToCart(product._id)} className="flex-1 bg-[#2d2d2d] text-white text-xs py-2 rounded-lg"><ShoppingCart size={12} className="inline mr-1" />Add</button>
                <button onClick={() => handleRemove(product._id)} className="text-red-500 text-xs px-2 border border-red-200 rounded-lg">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ company_name: '', buyer_name: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ company_name: user.company_name || '', buyer_name: user.buyer_name || '', phone: user.phone || '' });
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authAPI.updateProfile(form);
      setUser(res.data.user);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  return (
    <div>
      <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Account Settings</h2>
      <form onSubmit={handleSave} className="max-w-md space-y-4">
        <div>
          <label className="text-xs text-[#6f6a63] block mb-1">Company Name</label>
          <input type="text" value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
        </div>
        <div>
          <label className="text-xs text-[#6f6a63] block mb-1">Buyer Name</label>
          <input type="text" value={form.buyer_name} onChange={(e) => setForm(f => ({ ...f, buyer_name: e.target.value }))} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
        </div>
        <div>
          <label className="text-xs text-[#6f6a63] block mb-1">Phone</label>
          <input type="tel" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
        </div>
        <button type="submit" disabled={saving} className="bg-[#2d2d2d] text-white px-6 py-2.5 rounded-lg text-sm disabled:opacity-50">{saving ? 'Saving...' : 'Save Changes'}</button>
      </form>
    </div>
  );
}
